// public/scripts/tagging.js

import { appData, state } from '../../app/state/state.js';
import {
    getUsersWithPermission,
    getUserDisplayName,
    isUserInactive,
} from "../utils/userDisplay.js";
import {
    findItem,
    getCollectionName,
} from "../utils/itemLookup.js";

let currentTextArea = null;
let tagDropdown = null;
let activeTaggingSession = null;
let activeIndex = -1;
const taggingSessions = new WeakMap();
let taggingSessionSequence = 0;

function sortTaggableUsers(users) {
  return [...users].sort((a, b) =>
    (a?.name || a?.email || a?.id || "")
      .localeCompare(b?.name || b?.email || b?.id || "", undefined, {
        sensitivity: "base",
      }),
  );
}

function getTaggingSession(textArea) {
  return taggingSessions.get(textArea) || null;
}

function setActiveTaggingSession(session) {
  activeTaggingSession = session;
  currentTextArea = session?.textArea || null;
}

function getTaggableUsers(itemId, itemCollectionName) {
  const allUsers = appData?.users || [];
  const allowedUserIds = new Set();

  if (itemCollectionName === 'tasks') {
      const task = findItem(itemId);
      if (task) {
          if (task.owner) {allowedUserIds.add(task.owner);}
          (task.assigned_users || []).forEach(id => allowedUserIds.add(id));

          if (task.parentId && task.parentType) {
              allUsers.forEach(u => {
                  if (u.role === 'admin') {allowedUserIds.add(u.id);}
              });

              const parentCollection = getCollectionName(task.parentType);
              const parentAllowedUsers = getUsersWithPermission(parentCollection);
              parentAllowedUsers.forEach(u => allowedUserIds.add(u.id));
          }
      }
  } else {
      allUsers.forEach(u => {
          if (u.role === 'admin') {allowedUserIds.add(u.id);}
      });

      const permissionKey = itemCollectionName === "ordering_items" ?
        "ordering" : itemCollectionName;
      const collectionAllowedUsers = getUsersWithPermission(permissionKey);
      collectionAllowedUsers.forEach(u => allowedUserIds.add(u.id));
  }

  return sortTaggableUsers(allUsers.filter((u) =>
    allowedUserIds.has(u.id) &&
    u.id !== state.currentUser?.uid &&
    !isUserInactive(u)
  ));
}

/**
 * Initializes the @tagging functionality on a specific textarea.
 * @param {HTMLTextAreaElement} textArea Textarea to tag-enable.
 * @param {string} itemId Parent item id.
 * @param {string} itemCollectionName Parent collection name.
 * @returns {Promise<void>}
 */
export async function initTagging(textArea, itemId, itemCollectionName) {
  if (!textArea || !itemId || !itemCollectionName) {return;}

  // Create dropdown if it doesn't exist
  if (!tagDropdown) {
    tagDropdown = document.createElement('div');
    tagDropdown.id = 'tag-dropdown';
    tagDropdown.className = 'hidden';
    tagDropdown.setAttribute('role', 'listbox');
    tagDropdown.setAttribute('aria-label', 'Mention suggestions');
    tagDropdown.style.display = 'none'; // Explicit inline style for Edge compatibility
    document.body.appendChild(tagDropdown);
  }

  const allTaggableUsers = getTaggableUsers(itemId, itemCollectionName);

  const previousSession = getTaggingSession(textArea);
  previousSession?.abortController?.abort();

  const session = {
      abortController: new AbortController(),
      allTaggableUsers,
      currentFilteredUsers: allTaggableUsers,
      itemCollectionName,
      itemId,
      textArea,
      sessionId: `tagging-session-${++taggingSessionSequence}`,
  };
  taggingSessions.set(textArea, session);
  setActiveTaggingSession(session);
  textArea.setAttribute('role', 'combobox');
  textArea.setAttribute('aria-autocomplete', 'list');
  textArea.setAttribute('aria-haspopup', 'listbox');
  textArea.setAttribute('aria-controls', tagDropdown.id);
  textArea.setAttribute('aria-expanded', 'false');

  // --- Attach Listeners ---
  const listenerOptions = { signal: session.abortController.signal };
  textArea.addEventListener('input', handleInput, listenerOptions);
  textArea.addEventListener('keydown', handleKeydown, listenerOptions);
  textArea.addEventListener('blur', () => {
      // Delay closing so click event on dropdown works
      setTimeout(() => {
          if (activeTaggingSession === session) {
              closeDropdown();
          }
      }, 200);
  }, listenerOptions);
  
  // Also close on scroll to avoid floating dropdowns detached from text
  textArea.addEventListener('scroll', () => {
      if (activeTaggingSession === session) {
          closeDropdown();
      }
  }, listenerOptions);
}

function closeDropdown() {
    if (tagDropdown && tagDropdown.style.display !== 'none') {
        tagDropdown.classList.add('hidden');
        tagDropdown.style.display = 'none'; // Explicit inline style for Edge compatibility
        activeIndex = -1;
    }
    currentTextArea?.setAttribute('aria-expanded', 'false');
    currentTextArea?.removeAttribute('aria-activedescendant');
}

/**
 * Hides any open tagging dropdown.
 * @returns {void}
 */
export function closeTaggingDropdown() {
    closeDropdown();
    activeTaggingSession = null;
    currentTextArea = null;
}

function handleKeydown(e) {
    const session = getTaggingSession(e.currentTarget);
    if (!session) {return;}
    setActiveTaggingSession(session);
    if (
        tagDropdown.style.display === 'none' ||
        tagDropdown.classList.contains('hidden') ||
        session.currentFilteredUsers.length === 0
    ) {return;}

    if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIndex = (activeIndex + 1) % session.currentFilteredUsers.length;
        updateActiveSelection();
    } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndex = (
            activeIndex - 1 + session.currentFilteredUsers.length
        ) % session.currentFilteredUsers.length;
        updateActiveSelection();
    } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (activeIndex > -1 && session.currentFilteredUsers[activeIndex]) {
            selectUser(session.currentFilteredUsers[activeIndex]);
        }
    } else if (e.key === 'Escape') {
        e.preventDefault();
        closeDropdown();
    }
}

function updateActiveSelection() {
  Array.from(tagDropdown.children).forEach((child, index) => {
    const isActive = index === activeIndex;
    child.classList.toggle('active', isActive);
    child.setAttribute('aria-selected', String(isActive));
  });
  const activeOption = tagDropdown.children[activeIndex];
  if (activeOption && currentTextArea) {
      currentTextArea.setAttribute('aria-activedescendant', activeOption.id);
  }
}

function handleInput(e) {
  const session = getTaggingSession(e.currentTarget);
  if (!session) {return;}
  setActiveTaggingSession(session);
  session.allTaggableUsers = getTaggableUsers(
    session.itemId,
    session.itemCollectionName,
  );
  const { value, selectionEnd } = currentTextArea;
  const textBeforeCursor = value.substring(0, selectionEnd);
  const taggedDisplayNames = new Set(
    session.allTaggableUsers
      .map((user) => getUserDisplayName(user, appData.users))
      .filter((displayName) => value.includes(`@${displayName}`)),
  );
  
  // Match @Word at the very end of the string
  const tagRegex = /@([a-zA-Z0-9 ]*)$/; 
  const match = textBeforeCursor.match(tagRegex);

  if (match) {
    const searchTerm = match[1].toLowerCase();
    const activeTagText = `@${match[1]}`;
    
    // Filter users based on search
    const users = sortTaggableUsers(session.allTaggableUsers.filter((user) =>
      (user?.name || "").toLowerCase().includes(searchTerm) &&
      (
        !taggedDisplayNames.has(getUserDisplayName(user, appData.users)) ||
        `@${getUserDisplayName(user, appData.users)}` === activeTagText
      )
    ));
    
    session.currentFilteredUsers = users;

    if (users.length > 0) {
        renderDropdown(users);
        // Position based on cursor
        const coords = getCursorCoordinates(currentTextArea, selectionEnd);
        positionDropdown(coords);
    } else {
        closeDropdown();
    }
  } else {
      closeDropdown();
  }
}

function renderDropdown(users) {
    tagDropdown.innerHTML = '';
    activeIndex = 0;

    users.forEach((user, index) => {
        const item = document.createElement('div');
        item.className = 'tag-dropdown-item';
        item.id = `${activeTaggingSession?.sessionId || "tagging"}-option-${index}`;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', String(index === 0));
        if (index === 0) {item.classList.add('active');}
        
        // Show "Name" (e.g. Alex Smith) in the list
        item.textContent = user.name; 
        
        item.addEventListener('mousedown', (e) => {
            e.preventDefault(); // Prevent blur
            selectUser(user);
        });
        
        tagDropdown.appendChild(item);
    });
    
    tagDropdown.classList.remove('hidden');
    tagDropdown.style.display = 'block'; // Explicit inline style for Edge compatibility
    currentTextArea?.setAttribute('aria-expanded', 'true');
    updateActiveSelection();
}

function selectUser(user) {
    if (!currentTextArea) {
        closeDropdown();
        return;
    }
    const { value, selectionEnd } = currentTextArea;
    const textBeforeCursor = value.substring(0, selectionEnd);
    
    const tagStartIndex = textBeforeCursor.lastIndexOf('@');
    if (tagStartIndex === -1) {
        closeDropdown();
        return;
    }

    // Insert the short display name (e.g. @Alex S); save handlers convert it later.
    const displayName = getUserDisplayName(user, appData.users);
    const tagText = `@${displayName}`;
    
    const textBefore = value.substring(0, tagStartIndex);
    const textAfter = value.substring(selectionEnd);

    currentTextArea.value = `${textBefore}${tagText} ${textAfter}`;
    
    const newCursorPos = (textBefore + tagText + " ").length;
    currentTextArea.focus();
    currentTextArea.setSelectionRange(newCursorPos, newCursorPos);

    closeDropdown();
    currentTextArea.dispatchEvent(new Event('input', { bubbles: true }));
}

function positionDropdown(coords) {
    // Offset slightly so it doesn't cover the text
    const top = coords.top + 20;
    const left = coords.left;
    
    tagDropdown.style.top = `${top}px`;
    tagDropdown.style.left = `${left}px`;
    tagDropdown.style.width = '200px'; // Fixed width for cleaner look
}

/**
 * Calculates the pixel coordinates of the cursor within a textarea.
 * Uses a "Mirror Div" technique.
 */
function getCursorCoordinates(textarea, position) {
    const mirror = document.createElement('div');
    const style = window.getComputedStyle(textarea);
    
    // Copy all relevant styles
    Array.from(style).forEach(key => {
        mirror.style.setProperty(key, style.getPropertyValue(key), style.getPropertyPriority(key));
    });
    
    mirror.style.position = 'absolute';
    mirror.style.visibility = 'hidden';
    mirror.style.top = '0';
    mirror.style.left = '0';
    mirror.style.width = style.width; // Important for wrapping
    mirror.style.height = 'auto';
    mirror.style.overflow = 'hidden';
    
    // Content up to cursor
    const textContent = textarea.value.substring(0, position);
    mirror.textContent = textContent;
    
    // Append a marker span to find the end position
    const marker = document.createElement('span');
    marker.textContent = '|';
    mirror.appendChild(marker);
    
    document.body.appendChild(mirror);
    
    const rect = textarea.getBoundingClientRect();
    // Calculate relative position inside the mirror
    const relativeTop = marker.offsetTop;
    const relativeLeft = marker.offsetLeft;
    
    // Account for scroll position of the textarea
    const scrollTop = textarea.scrollTop;
    
    document.body.removeChild(mirror);

    return {
        top: rect.top + relativeTop - scrollTop,
        left: rect.left + relativeLeft
    };
}
